/**
 * The shapes tool's own controls (SRS C4), shown while it is in hand.
 *
 * Which of the six shapes to draw, and — for the polygon — how many sides it has.
 * The colour and the thickness come from the same controls the pen uses, because a
 * shape is drawn in the ink the teacher already chose.
 */

import type { ShapeKind } from '@opal/shared';
import { SHAPE_KINDS } from '@opal/shared';

import {
  MAX_POLYGON_SIDES,
  MIN_POLYGON_SIDES,
} from '../../board/shape-geometry.js';
import { useBoardStore } from '../../board/store.js';

const LABELS: Readonly<Record<ShapeKind, string>> = {
  line: 'Line',
  arrow: 'Arrow',
  rectangle: 'Rectangle',
  circle: 'Circle',
  triangle: 'Triangle',
  polygon: 'Polygon',
};

/** The little drawing on each button, so it says what it makes without reading. */
const MARKS: Readonly<Record<ShapeKind, readonly string[]>> = {
  line: ['M4 19 20 5'],
  arrow: ['M4 19 20 5', 'M13 5h7v7'],
  rectangle: ['M3.5 6h17v12h-17Z'],
  circle: ['M20 12a8 8 0 1 1-16 0a8 8 0 0 1 16 0Z'],
  triangle: ['M12 4.5 20.5 19.5H3.5Z'],
  polygon: ['M12 3.5 20.1 9.4 17 19H7L3.9 9.4Z'],
};

function ShapeMark({ kind }: { readonly kind: ShapeKind }) {
  return (
    <svg
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {MARKS[kind].map((path) => (
        <path key={path} d={path} />
      ))}
    </svg>
  );
}

/** SRS C4 — the six shapes the tool offers. */
function KindPicker() {
  const kind = useBoardStore((state) => state.shapeKind);
  const setKind = useBoardStore((state) => state.setShapeKind);

  return (
    <div className="grid grid-cols-3 gap-2">
      {SHAPE_KINDS.map((value) => {
        const active = kind === value;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            title={LABELS[value]}
            onClick={() => {
              setKind(value);
            }}
            className={`flex min-h-touch flex-col items-center justify-center gap-1 rounded-md border px-1 py-2 text-xs transition-colors duration-fast ${
              active
                ? 'border-primary bg-primary-subtle text-primary'
                : 'border-border bg-surface text-text-primary hover:bg-surface-hover'
            }`}
          >
            <ShapeMark kind={value} />
            {LABELS[value]}
          </button>
        );
      })}
    </div>
  );
}

/** How many sides the polygon has. Only shown while the polygon is chosen. */
function SidesSlider() {
  const sides = useBoardStore((state) => state.polygonSides);
  const setSides = useBoardStore((state) => state.setPolygonSides);

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-medium text-text-secondary">Sides</h3>
        <span className="font-mono text-sm text-text-primary">{sides}</span>
      </div>
      <input
        type="range"
        min={MIN_POLYGON_SIDES}
        max={MAX_POLYGON_SIDES}
        step={1}
        value={sides}
        aria-label="Number of sides"
        onChange={(event) => {
          setSides(Number(event.target.value));
        }}
        className="w-full accent-primary"
      />
    </section>
  );
}

export function ShapeControls() {
  const kind = useBoardStore((state) => state.shapeKind);

  return (
    <>
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-medium text-text-secondary">Shape</h3>
        <KindPicker />
        <p className="text-xs text-text-secondary">
          Press where the shape starts and drag to where it ends. Pick Select
          afterwards to move, resize or turn it.
        </p>
      </section>
      {kind === 'polygon' ? <SidesSlider /> : null}
    </>
  );
}
